/**
 * Authentication Hook & Context
 * 
 * Provides auth state management using Supabase Auth.
 * Wraps the app to provide session/user info and auth methods.
 */

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";
import type { Session, User } from "@supabase/supabase-js";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null; user: User | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Detect auth params in URL (PKCE code or implicit-flow hash tokens).
    // When present we must keep loading=true until the exchange completes,
    // otherwise downstream components redirect before the session is established
    // and the code/token is lost — leaving the email unconfirmed.
    const url = new URL(window.location.href);
    const hasPendingAuth =
      url.searchParams.has("code") ||
      url.hash.includes("access_token") ||
      url.hash.includes("error");

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Only stop loading immediately if there are no auth params to process,
      // or if the session was already resolved (e.g. existing localStorage session).
      if (!hasPendingAuth || session) {
        setLoading(false);
      }
    });

    // Listen for auth changes — this fires once the PKCE code exchange
    // or hash-token extraction completes.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Track user identity in Sentry for error correlation
        if (session?.user) {
          Sentry.setUser({
            id: session.user.id,
            email: session.user.email,
          });
        } else {
          Sentry.setUser(null);
        }
      }
    );

    // Safety net: if the auth exchange doesn't resolve within 5 seconds
    // (e.g. expired code, network error), stop loading so the user isn't
    // stuck on a blank screen forever.
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (hasPendingAuth) {
      timeout = setTimeout(() => setLoading(false), 5000);
    }

    return () => {
      subscription.unsubscribe();
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? new Error(error.message) : null };
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return {
      error: error ? new Error(error.message) : null,
      user: data?.user ?? null,
    };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
